import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useEnrollmentPeriodOpenInfo } from '../../../../../hooks/use-get-periods-info/use-enrollment-period';
import { userSelector } from '../../../../../selectors';
import { useGetUserStatesInPeriods } from '../../../../../hooks/use-get-user-states-in-enrollment';

export enum PeriodTypeForChanges {
  PURCHASE = 'PURCHASE',
  OFFERING = 'OFFERING',
}

const useGetPeriodsForChanges = () => {
  const {
    currentOpAllowedActions,
    futureOpAllowedActions,
    currentOpPotentiallyAllowedActions,
    futureChangePeriodType,
  } = useSelector(userSelector);
  const { isEnrollmentOpenForPurchasePeriod } = useEnrollmentPeriodOpenInfo();
  const { userStateInFuturePeriodOpOrPp } = useGetUserStatesInPeriods();

  const currentPeriodTypeForChanges = PeriodTypeForChanges.PURCHASE;
  const currentPeriodAllowedActions = useMemo(
    () => currentOpAllowedActions ?? [],
    [currentOpAllowedActions],
  );

  const futurePeriodTypeForChanges = useMemo(() => {
    if (futureChangePeriodType) {
      return PeriodTypeForChanges[futureChangePeriodType];
    }

    return isEnrollmentOpenForPurchasePeriod
      ? PeriodTypeForChanges.PURCHASE
      : PeriodTypeForChanges.OFFERING;
  }, [isEnrollmentOpenForPurchasePeriod, futureChangePeriodType]);
  const futurePeriodAllowedActions = useMemo(() => {
    if (futurePeriodTypeForChanges === PeriodTypeForChanges.OFFERING) {
      return futureOpAllowedActions ?? [];
    }

    // If the next period is a purchase period and the user is enrolled there,
    // we expect that all actions allowed for the current OP for the plan
    // will be allowed for the next PP.
    return (
      (userStateInFuturePeriodOpOrPp.enrolledEspp &&
      !userStateInFuturePeriodOpOrPp.withdrawn
        ? currentOpPotentiallyAllowedActions
        : undefined) ?? []
    );
  }, [
    futurePeriodTypeForChanges,
    currentOpPotentiallyAllowedActions,
    futureOpAllowedActions,
    userStateInFuturePeriodOpOrPp.enrolledEspp,
    userStateInFuturePeriodOpOrPp.withdrawn,
  ]);

  return useMemo(
    () => ({
      currentPeriodTypeForChanges,
      currentPeriodAllowedActions,
      currentOpAllowedActions,
      futurePeriodTypeForChanges,
      futurePeriodAllowedActions,
    }),
    [
      currentPeriodTypeForChanges,
      currentPeriodAllowedActions,
      currentOpAllowedActions,
      futurePeriodTypeForChanges,
      futurePeriodAllowedActions,
    ],
  );
};

export default useGetPeriodsForChanges;
